import { isKnown } from './carSchema';

/**
 * The famous names attached to a car, joined (SPEC-008).
 *
 * The same two-file join `carCatalogue.js` performs one level up, for the same
 * reason: a person's connection to a car is a closed vocabulary value and rides
 * in `catalogue.json`, while their name, their job and their two paragraphs are
 * copy and ride in `prose.<lang>.json`. Neither half renders alone — the facts
 * are an id and a chip, the prose is words with nothing to attach them to.
 *
 * Pure and browser-free, like everything else in `domain/`: this is what lets a
 * suite assert the join over the REAL forty-two people without a DOM.
 */

/**
 * The prose fields a row cannot be drawn without.
 *
 * All four, and `background` is in the list rather than treated as a bonus
 * paragraph: a row that stated who somebody was and then stopped, or told an
 * anecdote about an unidentified person, would be a half-drawn row. The band
 * would rather show two complete people than three of which one is a stub — the
 * `joinCatalogue` argument (D-12) at one level down.
 */
const REQUIRED_PROSE = ['name', 'occupation', 'story', 'background'];

/** The most letters a monogram plate carries. Two initials, or one for a mononym. */
const INITIALS_MAX = 2;

function isFilled(value) {
    return typeof value === 'string' && value.trim() !== '';
}

/**
 * Why this person cannot be drawn — or `null` if they can.
 *
 * Separate from the join for the same reason `carRecordProblem` is: the drop has
 * to be explainable. A person who silently vanishes from a band is a content bug
 * nobody can see, because the band that would have named them still looks
 * complete at two rows.
 */
export function ownerRecordProblem(owner, prose) {
    if (!owner || typeof owner.id !== 'string' || !owner.id.trim()) {
        return 'the record carries no id';
    }

    // A connection the schema does not know would print `cars.owners.connection.
    // racer` on a chip beside a real person's name — and the chip is the ONLY
    // thing on the row separating "owned one" from "drove it on screen", so a
    // reader who sees it fail will read the row as ownership. That is the one
    // failure in this band with a cost beyond looking wrong.
    if (!isKnown('connection', owner.connection)) {
        return `the schema does not know connection="${owner.connection}"`;
    }

    if (!prose) {
        return 'no pack carries their prose';
    }

    const missing = REQUIRED_PROSE.filter((field) => !isFilled(prose[field]));

    return missing.length > 0 ? `their prose says nothing under ${missing.join(', ')}` : null;
}

/**
 * The band's rows, in authored order, for the people who can actually be drawn.
 *
 * Authored order rather than sorted, and it is editorial: the F40 leads with
 * Nigel Mansell because Ferrari gave him the car, and the Viper leads with Bob
 * Lutz because he is the reason the car exists. Sorting by name would put Eric
 * Clapton first and lose the argument each band is making.
 *
 * A bad person costs one ROW, never the band and never the car — `warn` is
 * defaulted so a suite can assert the drop was reported, which is the only thing
 * that makes a silent drop safe.
 */
export function famousOwners(car, { warn = console.warn } = {}) {
    const owners = Array.isArray(car?.owners) ? car.owners : [];
    const prose = car?.prose?.owners ?? {};

    // A person already seated. `validateOwners` refuses a repeat at build time
    // and says why: the band keys on the id, so a second row for the same person
    // shares a React key with the first and the two reconcile against one fiber
    // — which on this screen means a row torn down and rebuilt on every frame of
    // a swipe. The build is not the last word, though: the app FETCHES its
    // catalogue, deployed independently of the code reading it, so the runtime
    // join has to answer for the same document.
    const seated = new Set();

    return owners.reduce((rows, owner) => {
        const words = prose[owner?.id];
        const problem = seated.has(owner?.id)
            ? 'they are already on this card'
            : ownerRecordProblem(owner, words);

        if (problem) {
            warn(`Dropping ${owner?.id ?? '(unnamed)'} from ${car?.id ?? '(unnamed car)'}: ${problem}.`);

            return rows;
        }

        seated.add(owner.id);

        rows.push({
            id: owner.id,
            connection: owner.connection,
            name: words.name.trim(),
            occupation: words.occupation.trim(),
            story: words.story.trim(),
            background: words.background.trim(),
            portrait: ownerPortrait(owner),
        });

        return rows;
    }, []);
}

/**
 * Does this car have a band at all?
 *
 * Asked by the screen so the heading is ABSENT rather than standing over
 * nothing, which is `CarGallery`'s rule and SPEC-004 D-21's: "Most famous
 * owners" over an empty space reads as a car nobody wanted.
 *
 * It performs the join rather than counting `car.owners`, because a car whose
 * three people were all dropped has three records and no rows.
 */
export function hasFamousOwners(car, options) {
    return famousOwners(car, { warn: () => {}, ...options }).length > 0;
}

/**
 * The monogram a person's plate carries where no portrait has been drawn.
 *
 * First letter of the first word and of the last, capped at two: "Takaharu
 * Kobayakawa" is TK and a mononym is one letter. Derived rather than authored,
 * because an `initials` field in the content pack is a second spelling of the
 * name that nothing would ever check against the first.
 *
 * A function rather than a `text-transform` trick, because CSS cannot count
 * words — `:first-letter` would give the F of "Ferrari" and nothing else.
 */
export function ownerInitials(name) {
    const words = String(name ?? '').trim().split(/\s+/u).filter(Boolean);

    if (words.length === 0) {
        return '';
    }

    const letters = words.length === 1
        ? [words[0]]
        : [words[0], words[words.length - 1]];

    return letters
        .slice(0, INITIALS_MAX)
        // `toUpperCase`, not `toLocaleUpperCase`: this is `domain/`, which has to
        // answer the same way in every process. The locale-aware form reads the
        // HOST's locale — nothing to do with the language CON-002 puts in the
        // URL — so on a device set to Turkish it would return İC for Ian Callum
        // and two phones opening the same `/en/` address would show different
        // initials.
        .map((word) => [...word][0].toUpperCase())
        .join('');
}

/**
 * The `src` and `srcSet` for one person's portrait, or NULL where none exists.
 *
 * NULL is the ordinary answer today — there is no portrait artwork in this
 * repository, and the band draws a monogram plate for everybody. That is a
 * designed state rather than a hole (D-8), and it is why this returns null
 * instead of composing a path that would 404: an `<img>` with a broken `src` is
 * a grey box with an alt string in it, which is what a placeholder must never
 * look like.
 *
 * The widths come out of the RECORD, measured by `build-owner-portraits.js`,
 * never assumed here — the same rule and the same failure as `carArt.js`: a
 * `srcset` candidate nobody wrote is a 404 the browser only asks for on the
 * widest screens, which is where it is least likely to be noticed.
 *
 * Composed in the domain rather than beside `ui/cars/carArt.js` because it is
 * string arithmetic over a record and needs no browser. `carArt.js` lives in the
 * UI only as a seam awaiting `domain/cars/carMedia.js`, and its own comment says
 * so; a second file at that seam would be one more thing to move when it opens.
 */
export function ownerPortrait(owner) {
    const base = owner?.portrait?.base;
    // Every rung checked, not just the array's shape. `validateOwners` insists on
    // positive integers at build time, and this is the same document after a
    // hand-edit: `widths: [0]` would compose `…-0.webp` and a `0w` descriptor the
    // parser throws away, leaving a 404 as the only source — an empty circle with
    // no monogram, which is the exact state this null exists to prevent.
    const ladder = owner?.portrait?.widths;
    const widths = (Array.isArray(ladder) ? ladder : [])
        .filter((width) => Number.isInteger(width) && width > 0);

    if (!base || widths.length === 0) {
        return null;
    }

    const stem = `${base}/${owner.id}`;

    return {
        // The smallest rung as the fallback, exactly as `artSource` does it: a
        // browser that ignores `srcset` gets the cheapest file rather than the
        // largest.
        src: `${stem}-${widths[0]}.webp`,
        srcSet: widths.map((width) => `${stem}-${width}.webp ${width}w`).join(', '),
    };
}
